import axios from '@/lib/axios'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { ref } from 'vue'
import { ToastInterface, useToast } from 'vue-toastification'

import { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { HotelRate } from '@/interfaces/hotel'
import { PaginatedResponse } from '@/interfaces/paginatedResponse'

interface Props {
	immediate?: boolean
	params?: Params
	config?: Config
}

interface Config {
	errorHandling: boolean
}

interface Params {
	id: string | number
	page?: number
}

interface CreateParams {
	hotelId: string | number
	typeCode: string
	amount: string
	agreement: boolean
	currency: string
	currencyConversion: string
	startDate: string
	expirationDate: string
}

const getHotelRates = async (
	params: Params,
	errorHandling = true
): Promise<PaginatedResponse<HotelRate>> => {
	const { data } = await axios.get<PaginatedResponse<HotelRate>>(
		`/api/internal/hotels/${params.id}/rates?page=${params.page}`,
		{
			errorHandling,
		} as CustomAxiosRequestConfig
	)

	return data
}

const createHotelRate = async (
	rate: CreateParams,
	toast: ToastInterface,
	params: Params
) => {
	try {
		const { data } = await axios.post<HotelRate>(
			`/api/internal/hotels/${params.id}/rates`,
			rate
		)
		toast.success('Se creo correctamente la tarifa del hotel')

		return data
	} catch (error: any) {
		toast.error(error.response.data.message)
	}
}

const useHotelRates = (props: Props = {}) => {
	const toast = useToast()

	const { config } = props
	const { errorHandling = true } = config || {}

	const page = ref<Number | String>(1)

	const { data, isLoading, isError, refetch, isRefetching } = useQuery(
		['hotel-contacts', props.params],
		() =>
			getHotelRates(
				{
					id: props.params?.id!,
					page: Number(page.value),
				},
				errorHandling
			),
		{ enabled: props.immediate }
	)

	const createHotelRateMutation = useMutation((contact: CreateParams) =>
		createHotelRate(contact, toast, props.params!)
	)

	const setPage = (newPage: number) => {
		page.value = newPage
	}

	return {
		hotelRates: data,
		isLoading,
		isRefetching,
		isError,
		refetch,
		page,
		setPage,

		createHotelRate: createHotelRateMutation.mutate,
		createHotelRateAsync: createHotelRateMutation.mutateAsync,
		createHotelRateLoading: createHotelRateMutation.isLoading,
	}
}

export default useHotelRates
